import { useState } from 'react';
import { useAuthStore } from '@/store/auth';
import { useThemeStore } from '@/store/theme';
import { api } from '@/services/api';
import { Sun, Moon, Monitor, Save, Globe, Bell } from 'lucide-react';
import { cn } from '@/utils/cn';
import { PeopleSection } from '@/components/settings/PeopleSection';
import { StorageSection } from '@/components/settings/StorageSection';
import { DeleteAccountSection } from '@/components/settings/DeleteAccountSection';

export function SettingsPage() {
  const { user, fetchUser } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const [currency, setCurrency] = useState(user?.preferences?.currency || 'USD');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  const savePreferences = async () => {
    setSaving(true);
    setSaveStatus('idle');
    try {
      await api.patch('/auth/me', {
        preferences: { currency, theme },
      });
      await fetchUser();
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your preferences, people, and home history</p>
      </div>

      <section className="rounded-xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-3">
          <Sun className="h-5 w-5 text-muted-foreground" />
          <h2 className="font-semibold">Appearance</h2>
        </div>
        <div className="flex gap-3">
          {([
            { value: 'light', label: 'Light', icon: Sun },
            { value: 'dark', label: 'Dark', icon: Moon },
            { value: 'system', label: 'System', icon: Monitor },
          ] as const).map((opt) => (
            <button
              key={opt.value}
              onClick={() => setTheme(opt.value)}
              className={cn(
                'flex flex-1 flex-col items-center gap-2 rounded-lg border p-4 transition-all',
                theme === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
              )}
            >
              <opt.icon className="h-5 w-5" />
              <span className="text-sm">{opt.label}</span>
            </button>
          ))}
        </div>
      </section>

      <PeopleSection />

      <section className="rounded-xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-3">
          <Globe className="h-5 w-5 text-muted-foreground" />
          <h2 className="font-semibold">Currency & Locale</h2>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Default Currency</label>
          <p className="mb-2 text-xs text-muted-foreground">
            Used for dashboard totals. Individual transactions keep their original currency.
          </p>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="USD">USD — US Dollar</option>
            <option value="EUR">EUR — Euro</option>
            <option value="GBP">GBP — British Pound</option>
            <option value="JPY">JPY — Japanese Yen</option>
            <option value="CAD">CAD — Canadian Dollar</option>
            <option value="AUD">AUD — Australian Dollar</option>
            <option value="INR">INR — Indian Rupee</option>
            <option value="SGD">SGD — Singapore Dollar</option>
            <option value="CHF">CHF — Swiss Franc</option>
            <option value="HKD">HKD — Hong Kong Dollar</option>
          </select>
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-3">
          <Bell className="h-5 w-5 text-muted-foreground" />
          <h2 className="font-semibold">Notifications</h2>
        </div>
        <div className="space-y-3">
          <label className="flex items-center justify-between">
            <div>
              <span className="text-sm">Email notifications</span>
              <p className="text-xs text-muted-foreground">Backfill completion, account errors</p>
            </div>
            <input type="checkbox" defaultChecked={user?.preferences?.notificationEmail} className="h-4 w-4 rounded" />
          </label>
          <label className="flex items-center justify-between">
            <div>
              <span className="text-sm">Weekly expense digest</span>
              <p className="text-xs text-muted-foreground">Summary of new transactions and detected trips</p>
            </div>
            <input type="checkbox" defaultChecked={user?.preferences?.weeklyDigest} className="h-4 w-4 rounded" />
          </label>
          <label className="flex items-center justify-between">
            <div>
              <span className="text-sm">Trip detection alerts</span>
              <p className="text-xs text-muted-foreground">When a new trip is identified from your data</p>
            </div>
            <input type="checkbox" defaultChecked={true} className="h-4 w-4 rounded" />
          </label>
        </div>
      </section>

      <button
        onClick={savePreferences}
        disabled={saving}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
      >
        <Save className="h-4 w-4" />
        {saving ? 'Saving...' : 'Save Preferences'}
      </button>
      {saveStatus === 'saved' && <p className="text-center text-sm text-green-600 dark:text-green-400">Preferences saved.</p>}
      {saveStatus === 'error' && <p className="text-center text-sm text-destructive">Could not save preferences. Please try again.</p>}

      <StorageSection />
      <DeleteAccountSection />
    </div>
  );
}
